import { ENVIRONMENTS } from "../../config";
import type { Environment, SignalingServerResponse, SsmParameter } from "../../types";
import { isWithinOpeningHours, parseOpeningHours } from "./openingHours";

export type KioskStatus = "unknown" | "not-connected" | "closed" | "problem" | "connected" | "queued";

export interface KioskRow {
  id: string;
  solution?: string;
  environment?: Environment;
  status: KioskStatus;
}

export interface KioskParam {
  key: string;
  type: string;
  value: string;
}

// Kiosk SSM params live under `.../kiosk/<kiosk-id>/<param>`, where both the
// kiosk id (e.g. "Danhostel/Reception") and other parameter names (e.g.
// "stt/video/flip") can themselves contain slashes — so a generic "last
// segment is the param" split doesn't work. Instead anchor on the known leaf
// param names we care about: the kiosk id is everything between the fixed
// 2-segment prefix and a trailing "/persona", "/env", or "/opening_hours".
const KIOSK_FIELD_PARAMS = ["persona", "env", "opening_hours"] as const;
type KioskFieldParam = (typeof KIOSK_FIELD_PARAMS)[number];

type KioskFields = { persona?: string; env?: string; opening_hours?: string };

function extractKioskId(name: string, paramName: KioskFieldParam): string | null {
  const parts = name.split("/").filter(Boolean);
  if (parts.length < 4 || parts[parts.length - 1] !== paramName) return null;
  return parts.slice(2, -1).join("/");
}

export function groupKiosks(parameters: SsmParameter[]): Map<string, KioskFields> {
  const kiosks = new Map<string, KioskFields>();
  for (const param of parameters) {
    for (const field of KIOSK_FIELD_PARAMS) {
      const kioskId = extractKioskId(param.Name, field);
      if (!kioskId) continue;
      const entry = kiosks.get(kioskId) ?? {};
      entry[field] = param.Value;
      kiosks.set(kioskId, entry);
    }
  }
  return kiosks;
}

export function resolveEnvironment(env: string | undefined): Environment | undefined {
  const candidate = env?.toUpperCase() as Environment | undefined;
  return candidate && ENVIRONMENTS.includes(candidate) ? candidate : undefined;
}

export function kioskStatus(
  kioskId: string,
  demand: SignalingServerResponse | undefined,
  openingHours: string | undefined,
): KioskStatus {
  if (!demand) return "unknown";
  if (demand.stacks.some((s) => s.kioskId === kioskId)) return "connected";
  if (demand.queues.some((q) => q.items.some((i) => i.kioskId === kioskId))) return "queued";

  // No current session — refine "why" using opening hours, but only when we
  // can evaluate them with confidence. Never guess "problem" or "closed".
  const spec = openingHours && parseOpeningHours(openingHours);
  if (!spec) return "not-connected";
  const open = isWithinOpeningHours(spec, new Date());
  if (open === null) return "not-connected";
  return open ? "problem" : "closed";
}

// Same palette as the Demand tab's instance status colors (STATE_COLORS in InstancesTable.tsx).
export function statusColor(status: KioskStatus): string | undefined {
  switch (status) {
    case "connected":
      return "#b0ffb9";
    case "queued":
      return "#feffb0";
    case "problem":
      return "#ffb3b0";
    case "closed":
      return "grey";
    default:
      return undefined;
  }
}

export function renderStatus(status: KioskStatus): string {
  switch (status) {
    case "unknown":
      return "—";
    case "not-connected":
      return "Not connected";
    case "closed":
      return "Closed";
    case "problem":
      return "Problem";
    case "connected":
      return "Connected";
    case "queued":
      return "Queued";
  }
}

// All raw SSM parameters belonging to one kiosk id, keyed by their path
// relative to the kiosk id — e.g. "persona", "env", "stt/video/flip".
export function paramsForKiosk(parameters: SsmParameter[], kioskId: string): KioskParam[] {
  const result: KioskParam[] = [];
  for (const param of parameters) {
    const relative = param.Name.split("/").filter(Boolean).slice(2).join("/");
    if (!relative.startsWith(`${kioskId}/`)) continue;
    result.push({ key: relative.slice(kioskId.length + 1), type: param.Type, value: param.Value });
  }
  return result.sort((a, b) => a.key.localeCompare(b.key));
}
